"use client";

import * as React from "react";

import { FORMATS, parseUnit, reservedFor, reservedHeight, type Device, type FormatDef, type SizeSet, type SizeToken } from "@/lib/ads/config";
import { gpt, type GptRenderEndedEvent, type GptSlot } from "@/lib/ads/gpt";
import { useAdRuntime, useSlotSizes } from "./ad-provider";

/**
 * A display ad slot.
 *
 *   <AdSlot unit="LargeRectangle" />        auto-numbered in DOM order → LargeRectangle1, 2, …
 *   <AdSlot unit="LargeRectangle2" />       explicit position
 *   <AdSlot unit="HalfPage" />              desktop-only by its size map; not requested below lg
 *   <AdSlot unit="TopBanner" sizes={{ mobile: ["300x250"] }} />   per-slot size override
 *
 * Brand comes from the route (lib/ads/targeting.brandFromPath); the GAM path is
 * /<network>/Web/<Brand>/<Brand>_Web_<Unit> (LJ's TopBanner is aliased to LongBanner).
 *
 * Without a network code the slot renders `children` (the fallback creative the
 * signed-off page already shows) and still carries data-ad-slot / data-ad-unit /
 * data-ad-sizes. With one, it renders the GPT div with the tallest creative's
 * height reserved per band (no CLS); if GAM returns nothing (or GPT fails to load)
 * the slot reports `data-ad-state="empty"` and the enclosing band hides itself —
 * no placeholder, no 廣告 label (globals.css `[data-ad-band]:has(...)`).
 */
export function AdSlot({
  unit,
  sizes: override,
  className = "",
  style,
  children,
  onRender,
  legacyPath,
  fitOversize: fitProp,
  grow: growProp,
  fillWidth = false,
  keepEmpty = false,
  requestOverflow = false,
  showOverflow = false,
  textFallback = false,
}: {
  /** Scale a fixed-size creative down (proportionally, never cropped) when it is taller than the box.
   *  On by default for every fixed-height display slot (an accepted size taller than the band's first
   *  size — a 620x250 on a 160 band — used to spill over the page, 2026-09-21); growing boxes opt in
   *  and only scale past their cap. */
  fitOversize?: boolean;
  /** Growing box: the reserved height becomes a minimum and a taller creative / SafeFrame push
   *  expansion grows the box up to `grow` px (`true` = no cap). Defaults to the format's `grow`. */
  grow?: number | true;
  /** Scale the creative up to the slot's width (專題 right banners fill the cell). */
  fillWidth?: boolean;
  /** Pilot mode: absolute path of an existing unit on the client's network. */
  legacyPath?: string;
  unit: string;
  sizes?: Partial<SizeSet>;
  className?: string;
  style?: React.CSSProperties;
  /** Fallback creative — rendered only while ads are disabled (no network code). */
  children?: React.ReactNode;
  /** Unfilled native: leave the slot empty. Do not copy a creative from another slot. */
  keepEmpty?: boolean;
  /**
   * A native past the brand's unit count still asks GAM. While it waits the
   * box stays up; an empty answer shows the article behind it.
   */
  requestOverflow?: boolean;
  /** Keep a repeated native visible so an empty answer can copy a sibling creative. */
  showOverflow?: boolean;
  /**
   * Search text chips. The editorial label stays in the fallback and shows
   * when GAM sends nothing. Image sizes are not requested, so a logo creative
   * cannot replace the text.
   */
  textFallback?: boolean;
  onRender?: (e: GptRenderEndedEvent) => void;
}) {
  const { format, index } = React.useMemo(() => parseUnit(unit), [unit]);
  const def = FORMATS[format];
  const rt = useAdRuntime();
  const sizes = useSlotSizes(format, override);
  const grow = growProp ?? def.grow;
  // Native, out-of-page, overlay and parallax boxes size (or clip) their creative themselves.
  const fitOversize = fitProp ?? (!def.native && !def.outOfPage && !def.overlay && !def.parallax && !grow);
  const ref = React.useRef<HTMLDivElement>(null);
  // "fluid": a fluid creative (responsive banner, native) rendered — the box follows its height.
  const [state, setState] = React.useState<"pending" | "filled" | "fluid" | "empty">("pending");

  const onRenderRef = React.useRef(onRender);
  onRenderRef.current = onRender;

  // SafeFrame ignores a pushdown's expand when this slot is overflow:hidden.
  // The scoped size rule sets that, and slotRenderEnded is too late: the
  // creative has already expanded once and will not try again. An inline
  // visible value is in place before the creative boots. A close writes
  // hidden itself, and this effect does not put visible back over that.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof grow !== "number" || !el.closest(".hkej-topband")) return;
    if (el.style.overflow === "hidden") return;
    el.style.overflow = "visible";
  }, [grow]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return rt.register({
      el,
      format,
      index,
      sizes,
      outOfPage: Boolean(def.outOfPage),
      overlay: Boolean(def.overlay),
      grow,
      legacyPath,
      onRender: (e) => {
        setState(e.isEmpty ? "empty" : isFluidRender(e) ? "fluid" : "filled");
        onRenderRef.current?.(e);
        if (def.overlay) return; // the host (skin wallpaper / light box) sizes the creative itself
        if (!e.isEmpty) centerFrame(el);
        const servedH = Array.isArray(e.size) ? Number(e.size[1]) : 0;
        const servedW = Array.isArray(e.size) ? Number(e.size[0]) : 0;
        const tooWide = servedW > el.clientWidth + 1 && el.clientWidth > 0;
        if (grow) {
          // Growing box: the creative (or its later push expansion) sets the height; only a
          // creative taller than the cap, or wider than the column, is scaled down.
          // The page-top band slides that height change. Other grow slots (in-read,
          // search text) keep their own sizing.
          if (!e.isEmpty) {
            const topband = Boolean(el.closest(".hkej-topband"));
            const cap = typeof grow === "number" ? grow : undefined;
            const reserved = reservedFor(def, sizes[rt.device], rt.device);
            if (topband && cap) {
              // A fluid roadblock (the 1440×160 domination banner) reports no
              // fixed height. slideTopBand would pin the band at the 160px
              // reserve and centre the shorter 9:1 frame, which is the white
              // strip above and below it. Hug the frame instead.
              // A short fixed creative (a 320×50 in the 90px tablet band) is
              // centred the same way. A 970-wide billboard or pushdown still
              // slides through slideTopBand — hugging those would cancel the
              // expand.
              const wideFixed = servedW >= 900;
              if (isFluidRender(e)) fitFluidTopBand(el);
              else if (!wideFixed && servedH > 0 && servedH + 1 < reserved) hugShortTopBand(el);
              else {
                slideTopBand(el, reserved, cap, servedH);
                if (servedH > 0 && servedH <= 120) armTopbandReplay(el, e.slot, servedH);
              }
            } else watchGrowth(el, reserved);
          }
          const overCap = typeof grow === "number" && servedH > grow;
          if (fitOversize && !e.isEmpty && (overCap || tooWide)) fitToBox(el);
          else if (def.shrinkToCreative && !e.isEmpty && !isFluidRender(e) && servedH > 0) {
            // min-height from the scoped style would otherwise keep a short creative
            // in the tall reserved band (the in-article BigBanner).
            el.style.minHeight = "0px";
            el.style.height = `${servedH}px`;
          }
          return;
        }
        if (fillWidth && !e.isEmpty) fitFillWidth(el);
        else if (fitOversize && !e.isEmpty) fitToBox(el);
        if (!fillWidth && def.shrinkToCreative && !e.isEmpty && !isFluidRender(e)) shrinkToCreative(el, e);
      },
      onResolve: (c) => setCode(c),
    });
  }, [rt, format, index, sizes, def, legacyPath, fitOversize, grow, fillWidth]);

  React.useEffect(() => {
    if (!fillWidth) return;
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => fitFillWidth(el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillWidth]);

  // Search text creatives are trafficked at 5×1. The creative measures its own
  // words and reports the width; the chip then hugs that text. The listener stays
  // up across pending → filled so a measurement that arrives with the iframe is kept.
  React.useEffect(() => {
    if (format !== "SearchText") return;
    const onMsg = (e: MessageEvent) => {
      const el = ref.current;
      const data = e.data as { source?: string; w?: number; h?: number } | null;
      if (!el || !data || data.source !== "hkej-search-text" || !e.source) return;
      if (!searchTextMessageFrom(el, e.source as Window)) return;
      fitSearchText(el, Number(data.w), Number(data.h));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [format]);

  React.useEffect(() => {
    if (format !== "SearchText") return;
    if (state !== "empty" && state !== "blocked") return;
    const el = ref.current;
    if (el) clearSearchTextSize(el);
  }, [format, state]);

  const live = rt.enabled && (!rt.pilot || Boolean(legacyPath));
  const blocked = live && rt.gptFailed;
  const showFallback = !rt.preview && !live;
  const [code, setCode] = React.useState<string>("");
  const native = Boolean(def.native);
  const sizeClass = `hkej-ad-sz-${sizeKey(sizes)}${native && def.placeholder ? `-p${def.placeholder.desktop}` : ""}`;
  const stateAttr = rt.preview ? "preview" : !live ? "fallback" : blocked ? "blocked" : state;

  // GPT owns the slot div: `display()` replaces its contents, so nothing of ours may live
  // inside it. The scoped <style>, the fallback creative and the native fallback card are
  // siblings inside a wrapper that carries the caller's className / style and mirrors the
  // state for the CSS that shows the native fallback.
  return (
    <div className={`hkej-ad-wrap ${className}`} style={style} data-ad-wrap="" data-ad-wrap-state={stateAttr}>
      {live && !rt.preview && <SlotStyle cls={sizeClass} sizes={sizes} def={def} reserve={!native && !def.outOfPage && !def.overlay} grow={grow} placeholder={native ? def.placeholder : undefined} />}
      <div
        ref={ref}
        className={`hkej-ad ${live && !rt.preview ? sizeClass : ""} w-full`}
        data-ad-format={format}
        data-ad-auto={index === undefined ? "" : undefined}
        data-ad-sizes={sizeAttr(sizes)}
        data-ad-state={stateAttr}
        data-ad-keep-empty={keepEmpty ? "" : undefined}
        data-ad-request-overflow={requestOverflow ? "" : undefined}
        data-ad-show-overflow={showOverflow ? "" : undefined}
        data-ad-text-fallback={textFallback ? "" : undefined}
        data-ad-grow={grow ? (grow === true ? "" : String(grow)) : undefined}
      >
        {rt.preview ? <PreviewBox code={code || `${rt.brand}_${unit}`} sizes={sizes} device={rt.device} oop={Boolean(def.outOfPage)} grow={grow} /> : null}
      </div>
      {showFallback ? children : null}
      {/* Native positions: a blank box of the card's height while GAM answers, then either
          the creative or — when nothing serves — the editorial card / row (client rule:
          "blank while loading; if no ad is served, substitute a real article"). Shown by
          globals.css for `empty` / `blocked` only. */}
      {live && !rt.preview && (native || textFallback) ? <div className="hkej-ad-fallback">{children}</div> : null}
    </div>
  );
}

function sizeAttr(s: SizeSet): string {
  const f = (l: SizeToken[]) => (l.length ? l.join(",") : "-");
  return `d:${f(s.desktop)};t:${f(s.tablet)};m:${f(s.mobile)}`;
}

/**
 * A fixed-size creative taller (or wider) than its box (a 970x160 image in a 142px band on a 1280px
 * screen) is scaled down proportionally so the whole creative stays visible — the
 * band's height is the design, the advertiser's image is never cropped. Re-applied on
 * resize because the band height follows the viewport width.
 */
/** GPT's container is often left- or right-aligned once the iframe is `display:block`. */
function centerFrame(el: HTMLElement) {
  const c = el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
  if (!c) return;
  c.style.marginLeft = "auto";
  c.style.marginRight = "auto";
}

/**
 * Stretch a fixed-size creative to the slot width, keeping its aspect ratio.
 * The image lives in a cross-origin iframe, so widening that iframe only adds
 * empty space beside the bitmap. Scaling the iframe's box paints the bitmap
 * across the cell.
 */
function fitFillWidth(el: HTMLElement) {
  const c = el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
  const f = c?.querySelector("iframe");
  if (!c || !f) return;
  const w = parseFloat(f.getAttribute("width") || "") || f.offsetWidth;
  const h = parseFloat(f.getAttribute("height") || "") || f.offsetHeight;
  const boxW = el.clientWidth;
  if (!w || !h || !boxW) return;
  const k = boxW / w;
  const nh = Math.max(1, Math.round(h * k));
  // Scale from the top-left corner. A centre origin on a creative that is
  // wider than the cell shifts the bitmap toward the right and clips it; a
  // cell wider than the creative was cropping the scaled bottom edge.
  el.style.position = "relative";
  el.style.display = "block";
  el.style.overflow = "hidden";
  el.style.minHeight = "0px";
  el.style.height = `${nh}px`;
  c.style.position = "absolute";
  c.style.left = "0";
  c.style.top = "0";
  c.style.width = `${w}px`;
  c.style.height = `${h}px`;
  c.style.maxWidth = "none";
  c.style.margin = "0";
  c.style.transformOrigin = "top left";
  c.style.transform = `scale(${k})`;
  f.style.width = `${w}px`;
  f.style.height = `${h}px`;
}

function fitToBox(el: HTMLElement) {
  const apply = () => {
    const c = el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
    const f = c?.querySelector("iframe");
    if (!c || !f) return;
    const h = parseFloat(f.getAttribute("height") || "0") || f.offsetHeight;
    const w = parseFloat(f.getAttribute("width") || "0") || f.offsetWidth;
    const box = el.clientHeight, boxW = el.clientWidth;
    if (!h || !box) return;
    // Width too: a 300x250 in LJ's 276px rail used to stick 24px out of its column (2026-09-21).
    const k = Math.min(1, box / h, w && boxW ? boxW / w : 1);
    // The slot centres the creative's container vertically, so a taller creative already sits
    // (h - box) / 2 above the band; scaling about its centre keeps the shrunken creative exactly
    // inside the band. Scaling from the top edge left it 45px too high on a 970x250 (2026-09-14).
    // margin:auto cannot pull a box that is wider than the slot back past the left edge, so that
    // centre then sits (w - boxW) / 2 too far right (a 300px LREC in LJ's 256px rail). Measure
    // the layout centre with no transform and shift it onto the slot centre. The translate is
    // applied after the scale, so it is not itself scaled.
    c.style.marginLeft = "auto";
    c.style.marginRight = "auto";
    c.style.transform = "none";
    const slot = el.getBoundingClientRect();
    const laid = c.getBoundingClientRect();
    const dx = slot.width > 0 ? slot.left + slot.width / 2 - (laid.left + laid.width / 2) : 0;
    c.style.transformOrigin = "center center";
    const shift = Math.abs(dx) >= 0.5 ? `translateX(${dx}px) ` : "";
    c.style.transform = k < 1 ? `${shift}scale(${k})` : shift.trim();
  };
  requestAnimationFrame(apply);
  window.addEventListener("resize", apply);
}

/**
 * Growing box: once a creative is in, watch the iframe GPT injected. A SafeFrame push expansion
 * (pushdown) or a vendor tag resizing itself makes the iframe taller than the reserved height;
 * the `min-height` box follows by itself, this only marks the state for styling and probes.
 */
function watchGrowth(el: HTMLElement, reserved: number) {
  const mark = () => {
    const f = el.querySelector<HTMLIFrameElement>('div[id^="google_ads_iframe_"] iframe');
    if (!f) return;
    const h = f.offsetHeight || parseFloat(f.getAttribute("height") || "0");
    if (h > reserved + 2) el.setAttribute("data-ad-expanded", String(Math.round(h)));
    else el.removeAttribute("data-ad-expanded");
  };
  const attach = () => {
    const f = el.querySelector<HTMLIFrameElement>('div[id^="google_ads_iframe_"] iframe');
    if (!f || typeof ResizeObserver === "undefined") { mark(); return; }
    const ro = new ResizeObserver(mark);
    ro.observe(f);
    const c = f.parentElement;
    if (c) ro.observe(c);
    mark();
  };
  requestAnimationFrame(attach);
}

const TOP_BAND_SLIDE_MS = 450;

type TopBandWatch = {
  ro?: ResizeObserver;
  timer: number;
  release: number;
  nudge: number;
  track: number;
  onMsg?: (e: MessageEvent) => void;
};
const topBandWatch = new WeakMap<HTMLElement, TopBandWatch>();
const pokedFrames = new WeakSet<HTMLIFrameElement>();

/** Drop a previous top-band watcher so a new creative does not fight it. */
function clearTopBandWatch(el: HTMLElement) {
  const prev = topBandWatch.get(el);
  if (!prev) return;
  prev.ro?.disconnect();
  window.clearTimeout(prev.timer);
  window.clearTimeout(prev.release);
  window.clearInterval(prev.nudge);
  window.cancelAnimationFrame(prev.track);
  if (prev.onMsg) window.removeEventListener("message", prev.onMsg);
  topBandWatch.delete(el);
}

/**
 * A fixed creative shorter than the band it reserved (320×50 in the 90px
 * tablet band, 728×90 in the 160px desktop band). The reserve centred it and
 * left a blank strip above and below. The slot hugs the frame. The bitmap
 * stays its own size — a 320px image is not stretched to the viewport.
 */
function hugShortTopBand(el: HTMLElement) {
  clearTopBandWatch(el);
  const frame = el.querySelector<HTMLIFrameElement>('div[id^="google_ads_iframe_"] iframe');
  const declared = frame ? parseFloat(frame.getAttribute("height") || "0") || 0 : 0;
  const h = Math.round(Math.max(declared, frame?.offsetHeight || 0));
  if (h < 20 || h > 415) return;
  el.style.setProperty("height", `${h}px`, "important");
  el.style.setProperty("min-height", "0px", "important");
  el.style.justifyContent = "flex-start";
  el.style.overflow = "visible";
  const box = frame?.parentElement;
  if (box && box !== el) {
    box.style.setProperty("margin-top", "0px", "important");
    box.style.setProperty("margin-bottom", "0px", "important");
    box.style.setProperty("height", `${h}px`, "important");
  }
}

/**
 * Fluid page-top banner. The slot follows the creative's own height at the
 * current column width, so a 9:1 image fills the band with no strip above or
 * below and without scaling the bitmap. The height attribute is that height;
 * the slot's 160px reserve is what letterboxed it.
 */
function fitFluidTopBand(el: HTMLElement) {
  clearTopBandWatch(el);
  const apply = () => {
    if (!el.isConnected) return;
    const frame = el.querySelector<HTMLIFrameElement>('div[id^="google_ads_iframe_"] iframe');
    if (!frame) return;
    const declared = parseFloat(frame.getAttribute("height") || "0") || 0;
    const h = Math.round(declared > 20 ? declared : frame.offsetHeight);
    if (h < 20 || h > 415) return;
    el.style.setProperty("height", `${h}px`, "important");
    el.style.setProperty("min-height", "0px", "important");
    el.style.justifyContent = "flex-start";
    el.style.overflow = "visible";
    const box = frame.parentElement;
    if (box && box !== el) {
      // Free space in the 160px band was absorbed as auto vertical margins,
      // which is the white strip. The frame already spans the column width.
      box.style.setProperty("margin-top", "0px", "important");
      box.style.setProperty("margin-bottom", "0px", "important");
    }
  };
  apply();
  const frame = el.querySelector<HTMLIFrameElement>('div[id^="google_ads_iframe_"] iframe');
  if (!frame || typeof ResizeObserver === "undefined") return;
  const ro = new ResizeObserver(() => apply());
  ro.observe(frame);
  topBandWatch.set(el, { ro, timer: 0, release: 0, nudge: 0, track: 0 });
}

/** Iframe or the GPT container around it, whichever is taller. */
function topBandBox(el: HTMLElement): { frame: HTMLIFrameElement | null; height: number } {
  const container = el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
  const frame = container?.querySelector<HTMLIFrameElement>("iframe") ?? null;
  // The height attribute is the creative's real size. offsetHeight is already
  // the band while a billboard is being rewound, so the attribute has to win
  // or the slide never starts.
  const declared = frame ? parseFloat(frame.getAttribute("height") || "0") || 0 : 0;
  const frameH = frame ? Math.max(frame.offsetHeight, declared) : 0;
  const containerH = container ? container.offsetHeight : 0;
  return { frame, height: Math.max(frameH, containerH) };
}

/**
 * Page-top band. `height: auto` cannot be transitioned, and the band's own
 * rule sets height with !important, so each change is a pixel height written
 * the same way. The page below then slides instead of jumping. A short
 * creative that never grows stays in the reserved band. Once it has grown
 * (pushdown), closing keeps the expanded frame and eases the band down.
 * The collapsed creative is swapped in by the creative itself once this
 * slide has finished. Resizing the iframe at the start of the close is the
 * 0.1s jump: `height: auto` follows the frame before the slide can start.
 */
function slideTopBand(el: HTMLElement, reserved: number, cap: number, servedH = 0) {
  const prev = topBandWatch.get(el);
  if (prev) {
    prev.ro?.disconnect();
    window.clearTimeout(prev.timer);
    window.clearTimeout(prev.release);
    window.clearInterval(prev.nudge);
    window.cancelAnimationFrame(prev.track);
    if (prev.onMsg) window.removeEventListener("message", prev.onMsg);
  }
  const rect = Math.round(el.getBoundingClientRect().height || 0);
  // GPT reports the registered size (a billboard is already 250; a pushdown
  // stays 90 while its frame grows). `height: auto` has jumped a billboard to
  // that size before this runs, so rewind it to the reserved band and slide.
  const jumped = servedH > reserved + 24;
  const frameNow = topBandBox(el).height;
  // A pushdown reports the registered 90px size even after its frame has
  // grown. Rewind that, and a billboard, to the reserved band and slide.
  // Keeping the tall rect as `shown` skips the slide.
  const alreadyTall = frameNow > reserved + 24;
  // Floor at the reserved band. Skin mode drops the CSS min-height, so a
  // pushdown's 90px frame would snap the page up before the expand slides it
  // back down.
  let shown = jumped || alreadyTall ? reserved : Math.max(rect || 0, reserved);
  if (shown > 0) {
    el.style.transition = "none";
    el.style.setProperty("height", `${shown}px`, "important");
    el.style.setProperty("min-height", "0px", "important");
    // The slot stays overflow:visible. Clipping it makes SafeFrame cancel
    // the expansion and refill the unit. trackFrame() sizes the iframe box
    // to the band, and that box is what reveals the artwork.
    el.style.overflow = "visible";
    if (alreadyTall || jumped) el.style.justifyContent = "flex-start";
    void el.offsetHeight;
  }
  // Ease the slot, the GPT box, and the iframe with one duration. The new
  // edge of a growing iframe stays unpainted until the ease ends, so a
  // pushdown paints that edge black for the slide (the artwork is black).
  // Do not clip a taller iframe instead: SafeFrame then drops the creative.
  // Do not write the iframe height on every frame for the same reason.
  const fitFrame = (h: number, animate = false) => {
    const { frame } = topBandBox(el);
    const box = frame?.parentElement;
    const px = `${Math.max(1, Math.round(h))}px`;
    const tr = animate ? `height ${TOP_BAND_SLIDE_MS}ms ease` : "none";
    if (frame) {
      frame.style.setProperty("transition", tr, "important");
      frame.style.setProperty("height", px, "important");
    }
    if (box && box !== el) {
      box.style.setProperty("transition", tr, "important");
      box.style.setProperty("height", px, "important");
      box.style.setProperty("overflow", "hidden", "important");
    }
  };
  const releaseBox = () => {
    const { frame } = topBandBox(el);
    const box = frame?.parentElement;
    if (!box || box === el) return;
    const ih = frame ? frame.getBoundingClientRect().height : 0;
    const sh = el.getBoundingClientRect().height;
    // The expanded artwork stays in the iframe until the creative's own
    // timeout. Dropping the clip before that timeout paints 415 over the page.
    if (ih > sh + 8) return;
    box.style.removeProperty("overflow");
    box.style.removeProperty("background-color");
    frame?.style.removeProperty("background-color");
  };
  if ((alreadyTall || jumped) && frameNow > shown + 2) fitFrame(shown);
  // A pushdown's own script sets the iframe to 415 with no transition, then
  // asks the page to slide. That write paints before the message arrives, so
  // the artwork is already full size while the page is still in the reserved
  // band. Clip the GPT box to the band now. The slide below eases this same
  // box with the slot, one duration. The iframe stays free to grow, which is
  // how the first open learns that the frame is actually tall. A leaderboard
  // is taller than 100px and must not be clipped.
  if (!jumped && !alreadyTall && servedH > 0 && servedH <= 120) {
    const frame = topBandBox(el).frame;
    const box = frame?.parentElement;
    if (frame && box && box !== el && frame.offsetWidth >= 900 && frame.offsetHeight <= 100) {
      box.style.setProperty("transition", "none", "important");
      box.style.setProperty("height", `${shown}px`, "important");
      box.style.setProperty("overflow", "hidden", "important");
    }
  }
  // After a rewind, `shown` is the reserved height, so this stays false until
  // the iframe reports the tall creative and the band slides up to it.
  let expandedOnce = shown > reserved + 24;
  let collapseLock = false;
  let queued = 0;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const watch: TopBandWatch = { timer: 0, release: 0, nudge: 0, track: 0 };
  topBandWatch.set(el, watch);
  let tracking = false;
  // The host frame jumps to the full creative in one step. Pull that box back
  // to the band and keep it there while the band eases, so the artwork and the
  // page move together. The slot stays overflow:visible; clipping it makes
  // SafeFrame cancel the expansion.
  const trackFrame = () => {
    const h = Math.round(el.getBoundingClientRect().height);
    const { frame } = topBandBox(el);
    const ih = frame ? Math.round(frame.getBoundingClientRect().height) : 0;
    // The creative's own height write has no duration. If it jumps ahead of
    // the shared ease, put the frame back on the band once. Writing the
    // height on every frame makes SafeFrame drop the creative.
    if (ih > h + 2) fitFrame(h, false);
    if (Math.abs(h - shown) > 1) {
      watch.track = requestAnimationFrame(trackFrame);
      return;
    }
    tracking = false;
    releaseBox();
  };
  const apply = (h: number, animate: boolean) => {
    const next = Math.round(Math.min(Math.max(h, 0), cap));
    if (next < 1 || Math.abs(next - shown) < 2) return;
    const motion = animate && !reduce && Math.abs(next - shown) >= 24;
    el.querySelector("[data-pd-mask]")?.remove();
    if (!motion) {
      shown = next;
      el.style.transition = "none";
      el.style.setProperty("height", `${next}px`, "important");
      el.style.setProperty("min-height", "0px", "important");
      el.style.overflow = "visible";
      if (next > reserved + 2) el.style.justifyContent = "flex-start";
      fitFrame(next, false);
      releaseBox();
      void el.offsetHeight;
      return;
    }
    // Snap both to the painted height, then ease them together. The iframe
    // ease leaves its new edge unpainted, so the pushdown's box and frame
    // are black for the duration: that edge is the artwork's own background,
    // and a white one reads as a gap above the bar.
    const from = Math.round(el.getBoundingClientRect().height) || shown;
    el.style.transition = "none";
    fitFrame(from, false);
    el.style.setProperty("height", `${from}px`, "important");
    void el.offsetHeight;
    shown = next;
    el.style.transition = `height ${TOP_BAND_SLIDE_MS}ms ease`;
    el.style.setProperty("height", `${next}px`, "important");
    el.style.setProperty("min-height", "0px", "important");
    el.style.overflow = "visible";
    if (next > reserved + 2) el.style.justifyContent = "flex-start";
    fitFrame(next, true);
    // A growing iframe leaves its new edge unpainted, so the page white
    // shows between the artwork and the bar. The pushdown artwork is black.
    if (next >= 300 || from >= 300) {
      const { frame } = topBandBox(el);
      const box = frame?.parentElement;
      frame?.style.setProperty("background-color", "#000", "important");
      if (box && box !== el) box.style.setProperty("background-color", "#000", "important");
    }
    tracking = true;
    window.cancelAnimationFrame(watch.track);
    watch.track = requestAnimationFrame(trackFrame);
  };
  const commit = () => {
    const { frame } = topBandBox(el);
    if (!frame || queued < 1 || collapseLock) return;
    if (queued > reserved + 2) {
      expandedOnce = true;
      el.setAttribute("data-ad-expanded", String(Math.round(queued)));
    } else el.removeAttribute("data-ad-expanded");
    const target = expandedOnce ? queued : Math.max(queued, reserved);
    if (expandedOnce && target + 2 < shown) {
      beginCollapse(target);
      return;
    }
    apply(target, true);
  };
  // Rewind to the height we are showing, then slide. Doing this in the same
  // frame as the resize means the snapped `height: auto` never paints.
  const beginCollapse = (target: number) => {
    if (collapseLock || target + 2 >= shown) return;
    expandedOnce = true;
    collapseLock = true;
    const from = Math.round(shown);
    el.style.transition = "none";
    el.style.setProperty("height", `${from}px`, "important");
    el.style.setProperty("min-height", "0px", "important");
    el.style.overflow = "visible";
    void el.offsetHeight;
    apply(target, true);
    watch.release = window.setTimeout(() => {
      collapseLock = false;
      const height = topBandBox(el).height;
      // The expanded frame is meant to stay until the slide ends, so a frame
      // that is still tall here is that artwork, not a new expand. A real
      // re-open is still tall a moment later and then slides back down.
      if (height > shown + 24) {
        watch.release = window.setTimeout(() => {
          if (!el.isConnected || collapseLock) return;
          if (topBandBox(el).height > shown + 24) mark();
          else {
            el.style.overflow = "visible";
            releaseBox();
          }
        }, 80);
        return;
      }
      // The next expand() is ignored while this slot is overflow:hidden.
      el.style.overflow = "visible";
      releaseBox();
      mark();
    }, TOP_BAND_SLIDE_MS + 60);
  };
  // The pushdown creative keeps the expanded artwork up and asks the page to
  // slide. It swaps to the collapsed image only after TOP_BAND_SLIDE_MS.
  const onMsg = (e: MessageEvent) => {
    const data = e.data as { source?: string; action?: string; to?: number } | null;
    if (!el.isConnected || !data || data.source !== "hkej-pushdown" || !e.source) return;
    if (!searchTextMessageFrom(el, e.source as Window)) return;
    const to = Number(data.to);
    if (data.action === "expand") {
      const target = Number.isFinite(to) && to > 0 ? Math.min(to, cap) : cap;
      collapseLock = false;
      window.clearTimeout(watch.release);
      // Re-open after a close. The close leaves a height lock on the iframe
      // so the 90px image can ease with the page, and SafeFrame cannot grow
      // past that lock. The creative has already swapped back to the tall
      // artwork, so this slide drives the frame. Waiting for the frame to
      // report 415 first leaves the cropped bar on screen.
      if (expandedOnce && shown + 24 < target) {
        el.setAttribute("data-ad-expanded", String(Math.round(target)));
        apply(target, true);
        return;
      }
      // First open. The frame may still be the registered 90px. Growing the
      // band now opens a blank gap. The observer starts the slide once the
      // frame is tall.
      if (topBandBox(el).height + 2 >= target) {
        expandedOnce = true;
        el.setAttribute("data-ad-expanded", String(Math.round(target)));
        apply(target, true);
      }
      return;
    }
    if (data.action !== "collapse") return;
    beginCollapse(Number.isFinite(to) && to > 0 ? to : 90);
  };
  watch.onMsg = onMsg;
  window.addEventListener("message", onMsg);
  // Grow in this same frame, before paint, so a 415px frame cannot sit on
  // the page while the band is still at the reserved height. A shrink waits:
  // the pushdown reports 90 for a moment on the way to 415, and that reading
  // is not a close. The close message does not use this timer.
  const mark = () => {
    if (collapseLock || tracking) return;
    const { height } = topBandBox(el);
    if (height < 1) return;
    queued = height;
    window.clearTimeout(watch.timer);
    if (height > shown + 2) {
      el.style.justifyContent = "flex-start";
      commit();
      return;
    }
    watch.timer = window.setTimeout(commit, 80);
  };
  const attach = () => {
    const { frame } = topBandBox(el);
    if (!frame || typeof ResizeObserver === "undefined") {
      mark();
      return;
    }
    const ro = new ResizeObserver(mark);
    watch.ro = ro;
    ro.observe(frame);
    const container = frame.parentElement;
    if (container) ro.observe(container);
    mark();
  };
  requestAnimationFrame(attach);
  // The tall frame was pulled back to the reserved band above, so the
  // observer no longer sees it. Slide back up to the height it actually is.
  if ((alreadyTall || jumped) && frameNow > shown + 2) {
    queued = frameNow;
    requestAnimationFrame(() => {
      if (!collapseLock && !tracking) commit();
    });
  }
  // SafeFrame runs the creative's register() callback on the next host
  // geometry update, and it drops updates for about 250ms after the frame is
  // created. A scroll inside that window does nothing, so the pushdown stays
  // cropped at 970×90 (關閉 on the top of the tall artwork). One scroll just
  // after the quiet period delivers the callback. A resize would also, and
  // GPT then refills the slot with a different creative. The crop is covered
  // until that scroll, so first paint is not the collapsed bar.
  if (!jumped && !alreadyTall && servedH > 0 && servedH <= 120) {
    const poke = () => {
      if (!el.isConnected || topBandBox(el).height > reserved + 24 || el.hasAttribute("data-ad-expanded")) return;
      window.dispatchEvent(new Event("scroll"));
    };
    const started = performance.now();
    const arm = () => {
      const { frame } = topBandBox(el);
      if (!el.isConnected || performance.now() - started > 2000) return;
      // Wait until the frame has its registered height. An earlier scroll is
      // inside SafeFrame's quiet period and is ignored.
      if (!frame || frame.offsetHeight < 80) {
        watch.nudge = window.setTimeout(arm, 40);
        return;
      }
      if (!pokedFrames.has(frame)) {
        pokedFrames.add(frame);
        // Wide and only as tall as the registered pushdown. A leaderboard
        // that already has its real height is left alone.
        const wide = frame.offsetWidth >= 900;
        const cropped = frame.offsetHeight <= 100;
        if (wide && cropped && !el.querySelector("[data-pd-mask]")) {
          if (getComputedStyle(el).position === "static") el.style.position = "relative";
          const mask = document.createElement("div");
          mask.setAttribute("data-pd-mask", "");
          const bg = getComputedStyle(el.closest(".hkej-topband") || el).backgroundColor;
          const fill = bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent" ? bg : "#fff";
          mask.style.cssText = `position:absolute;inset:0;background:${fill};z-index:5;pointer-events:none;`;
          el.appendChild(mask);
          window.setTimeout(() => {
            if (!el.hasAttribute("data-ad-expanded")) mask.remove();
          }, 1200);
        }
        window.setTimeout(poke, 280);
        window.setTimeout(poke, 520);
        window.setTimeout(poke, 900);
      }
    };
    arm();
  }
}

/**
 * A pushdown registers at 970×90 and expands itself. SafeFrame ignores that
 * first expand when the band rendered above the viewport (a reload that
 * restores a mid-page scroll), so the tall creative stays cropped in the
 * collapsed frame until something calls expand() again. Replay the slot once,
 * when it is actually on screen. A banner that rendered in view and stayed
 * short is a static leaderboard and is left alone.
 */
function armTopbandReplay(el: HTMLElement, slot: GptSlot, servedH: number) {
  if (servedH <= 0 || servedH > 120) return;
  if (el.getAttribute("data-ad-grow-replay")) return;
  // A SafeFrame push swaps the iframe for a moment. Height 0 in that gap is
  // not a short leaderboard, and refreshing here rotates the creative away.
  const grown = () => topBandBox(el).height > 200 || el.hasAttribute("data-ad-expanded");
  const inView = () => {
    const r = el.getBoundingClientRect();
    if (r.height < 1) return false;
    const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    return visible >= Math.min(r.height, 80) * 0.5;
  };
  const replay = () => {
    if (!el.isConnected || grown() || el.getAttribute("data-ad-grow-replay")) return;
    el.setAttribute("data-ad-grow-replay", "1");
    gpt((g) => g.pubads().refresh([slot]));
  };
  const whenVisible = () => {
    if (!el.isConnected || grown()) return;
    if (inView()) {
      replay();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
      io.disconnect();
      window.setTimeout(() => {
        if (grown()) return;
        replay();
      }, 200);
    }, { threshold: [0.5] });
    io.observe(el);
  };
  // Scroll restoration can jump the page after the band has already rendered at
  // the top, which is when SafeFrame drops the expand. Catch that jump as well
  // as a band that was off-screen from the start. A short static leaderboard
  // that stays in view is not replayed.
  const onScroll = () => {
    if (!el.isConnected || grown()) {
      window.removeEventListener("scroll", onScroll);
      return;
    }
    if (!inView()) {
      window.removeEventListener("scroll", onScroll);
      whenVisible();
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.setTimeout(() => window.removeEventListener("scroll", onScroll), 1500);
  window.setTimeout(() => {
    window.removeEventListener("scroll", onScroll);
    if (!el.isConnected || grown() || inView()) return;
    whenVisible();
  }, 600);
}

/**
 * A slot that reserves the tallest of its sizes (HalfPage: 300x600) but receives a shorter
 * creative (300x250) takes the creative's height once it has rendered, so the rail does not
 * carry 350px of white. The rule stays "fixed height" for banners, whose sizes differ little.
 */
function shrinkToCreative(el: HTMLElement, e: GptRenderEndedEvent) {
  const h = Array.isArray(e.size) ? Number(e.size[1]) : 0;
  if (!(h > 0)) return;
  // Follow the served height in both directions. The bottom banner rotates
  // every 10s; shrinking only left a 320x100 creative inside the 50px box
  // the previous 320x50 had set, so the taller creative was clipped.
  el.style.minHeight = "0px";
  el.style.height = `${h}px`;
}

/** A search-text chip hugs the words. 640px is a long label, not a banner. */
const SEARCH_TEXT_MAX_W = 640;

function searchTextMessageFrom(slot: HTMLElement, source: Window): boolean {
  for (const frame of slot.querySelectorAll("iframe")) {
    if (frame.contentWindow === source) return true;
    try {
      if (source.parent === frame.contentWindow) return true;
    } catch {
      /* a cross-origin parent walk can throw; the next frame is checked instead */
    }
  }
  return false;
}

function clearSearchTextSize(el: HTMLElement) {
  el.style.width = "";
  el.style.maxWidth = "";
  el.style.height = "";
  el.style.minHeight = "";
  const wrap = el.parentElement;
  if (wrap) {
    wrap.style.width = "";
    wrap.style.flex = "";
  }
}

function fitSearchText(el: HTMLElement, w: number, h: number) {
  if (!Number.isFinite(w) || w < 1) return;
  const width = Math.min(SEARCH_TEXT_MAX_W, Math.ceil(w));
  const height = Number.isFinite(h) && h >= 16 && h <= 40 ? Math.ceil(h) : 20;
  const wrap = el.parentElement;
  if (wrap) {
    wrap.style.width = `${width}px`;
    wrap.style.flex = "0 0 auto";
  }
  el.style.width = `${width}px`;
  el.style.maxWidth = `${width}px`;
  el.style.height = `${height}px`;
  el.style.minHeight = "0px";
  const box = el.querySelector<HTMLElement>('div[id^="google_ads_iframe_"]');
  if (box) {
    box.style.width = `${width}px`;
    box.style.height = `${height}px`;
  }
  const iframe = el.querySelector<HTMLIFrameElement>("iframe");
  if (iframe) {
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;
    // A friendly frame can be retargeted here. The trafficked creative's
    // own target wins when the frame is cross-origin.
    try {
      iframe.contentDocument?.querySelectorAll("a").forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
    } catch {
      /* cross-origin */
    }
  }
}

/** GPT reports a fluid creative's size as the string "fluid" (or no array at all). */
function isFluidRender(e: GptRenderEndedEvent): boolean {
  const z = e.size;
  return !Array.isArray(z) || z.length < 2 || z[0] === 0 || String(z[0]) === "fluid";
}

function sizeKey(s: SizeSet): string {
  return `${s.desktop.join("_")}-${s.tablet.join("_")}-${s.mobile.join("_")}`.replace(/[^a-zA-Z0-9_-]/g, "") || "none";
}

/**
 * Responsive rules per size signature (shared by every slot with the same sizes, so the
 * duplicated <style> elements are identical and harmless): on a band with sizes the slot
 * is a centred flex box that reserves the expected creative's height for good; on a band
 * with no sizes it is not rendered at all. `[data-ad-state="blocked"]` (ads blocked) hides
 * it, and globals.css hides the whole band with it. Written as a scoped <style> because the
 * heights differ per unit and Tailwind v4 only emits literal classes.
 */
function SlotStyle({ cls, sizes, def, reserve, grow, placeholder }: { cls: string; sizes: SizeSet; def: FormatDef; reserve: boolean; grow?: number | true; placeholder?: Record<Device, number> }) {
  const rule = (band: SizeToken[], device: Device) => {
    if (band.length === 0) return "display:none;";
    // Display units: a FIXED height (the expected creative's), not a minimum — a shorter
    // creative is centred in it and a fluid one is capped by it, so nothing ever moves
    // (client, 2026-09-09: "even if the actual height is smaller, keep the fixed height").
    // Growing boxes (client decision 2026-09-16, high-impact formats): the same height as a
    // MINIMUM, so a billboard, a pushdown's push expansion or a self-sizing outstream tag can
    // make the box taller — up to the cap, past which the creative is scaled instead.
    const h = reserve ? reservedFor(def, band, device) : 0;
    // Always written, even as 0 / auto: a wider band must undo the narrower band's value
    // (InRead reserves 250 on phones and nothing on desktop).
    const height = grow
      ? `height:auto;min-height:${h}px;${typeof grow === "number" ? `max-height:${grow}px;overflow:hidden;` : "max-height:none;"}`
      : reserve ? (h ? `height:${h}px;min-height:0;` : "height:auto;min-height:0;") : "";
    // A wider band must restore `display`: the narrower band's `display:none` cascades
    // upward otherwise, the slot has no client rect, and the provider never defines it.
    return `display:flex;flex-direction:column;align-items:center;justify-content:center;${height}`;
  };
  const sel = `.${cls}`;
  // Native units: a blank box of the card's / row's height while GAM answers; the box is
  // released when the creative renders (it sets its own height) or the fallback shows.
  const ph = placeholder
    ? `${sel}[data-ad-state="pending"]{min-height:${placeholder.mobile}px;}@media(min-width:768px){${sel}[data-ad-state="pending"]{min-height:${placeholder.tablet}px;}}@media(min-width:1280px){${sel}[data-ad-state="pending"]{min-height:${placeholder.desktop}px;}}`
    : "";
  // The box never changes height once painted — a fluid creative on a display slot has
  // to be designed at the band's height (the responsive top banner is 160px tall and
  // covers its width; see native-styles/zTEST_Revamp_Banner.html), otherwise the page
  // would either jump or show space above and below (client, 2026-09-09).
  // An empty size band hides the slot itself and the 廣告 band around it, or a
  // phone-only unit leaves a caption on tablet and desktop (and the reverse).
  // The hide is scoped to THAT band's width. A bare `display:none !important`
  // on the narrow band was winning over the wider band's `display:flex`, so a
  // desktop-only unit (HalfPage) and a banner with no phone size (BigBanner)
  // stayed hidden at every width.
  const band = `[data-ad-band]:has(${sel})`;
  const bandRule = (list: SizeToken[]) => (list.length === 0 ? "display:none!important;" : "display:flex;");
  const css = `${sel}{${rule(sizes.mobile, "mobile")}}@media(min-width:768px){${sel}{${rule(sizes.tablet, "tablet")}}}@media(min-width:1280px){${sel}{${rule(sizes.desktop, "desktop")}}}@media(max-width:767px){${band}{${bandRule(sizes.mobile)}}}@media(min-width:768px) and (max-width:1279px){${band}{${bandRule(sizes.tablet)}}}@media(min-width:1280px){${band}{${bandRule(sizes.desktop)}}}${ph}${sel}[data-ad-state="blocked"]{display:none!important;}`;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

/**
 * Design-review rendering (`?adpreview=1`, sticky for the session; `?adpreview=0` clears):
 * a dashed box the size of the tallest creative for the current band, labelled with the
 * unit code and the sizes that band requests. Bands with no sizes render nothing.
 */
function PreviewBox({ code, sizes, device, oop, grow }: { code: string; sizes: SizeSet; device: "desktop" | "tablet" | "mobile"; oop: boolean; grow?: number | true }) {
  const band = sizes[device];
  if (!band.length) return null;
  const fixed = band.filter((t) => t !== "fluid").map((t) => t.split("x").map((n) => parseInt(n, 10)) as [number, number]).filter(([w, h]) => w > 8 && h > 8);
  const [w, h] = fixed.length ? fixed.reduce((a, b) => (b[1] > a[1] ? b : a)) : [0, 0];
  const isText = !fixed.length && band.includes("fluid") && !/NativeAd|InBanner/.test(code);
  const isNativeCard = /NativeAd/.test(code);
  if (/Interscroller/.test(code) && band.includes("320x480")) {
    // the 250px window with the 320x480 creative behind it
    return (
      <div data-ad-preview="" style={{ position: "relative", width: "100%", height: 250, overflow: "hidden", border: "1px dashed #0d6b4b", background: "rgba(13,107,75,.06)", color: "#0d6b4b", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11 }}>
        {/* id shaped like GPT's container so the interscroller CSS / scroll reveal apply to the preview */}
        <div id="google_ads_iframe_preview" style={{ width: 320, height: 480, border: "1px dashed #0d6b4b", background: "repeating-linear-gradient(180deg, rgba(13,107,75,.16) 0 120px, rgba(13,107,75,.05) 120px 240px)", color: "#0d6b4b", fontSize: 11, textAlign: "center" }}>
          {[1, 2, 3, 4].map((k) => <div key={k} style={{ height: 120, lineHeight: "120px" }}>band {k} of 4</div>)}
        </div>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, textAlign: "center" }}>
          <strong style={{ fontWeight: 600 }}>{code}</strong>
          <span>320×480 revealed through a 250px window as you scroll</span>
          <span style={{ opacity: 0.7 }}>{band.join(" · ")}</span>
        </div>
      </div>
    );
  }
  const isNativeRow = /InBanner/.test(code); // list-row native; fixed sizes are legacy image fallbacks
  if (isNativeCard || isNativeRow) {
    // Native positions: mock the card / list row a native style would paint.
    return (
      <div data-ad-preview="" style={{ display: "flex", flexDirection: isNativeCard ? "column" : "row", gap: 12, width: "100%", padding: 10, border: "1px dashed #0d6b4b", background: "rgba(13,107,75,.06)", color: "#0d6b4b", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11 }}>
        <div style={{ background: "rgba(13,107,75,.18)", borderRadius: 2, ...(isNativeCard ? { width: "100%", aspectRatio: "16 / 9" } : { width: 160, minWidth: 160, aspectRatio: "16 / 9" }) }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
          <strong style={{ fontWeight: 600 }}>{code}</strong>
          <span style={{ height: 10, width: "85%", background: "rgba(13,107,75,.25)", borderRadius: 2 }} />
          <span style={{ height: 10, width: "60%", background: "rgba(13,107,75,.25)", borderRadius: 2 }} />
          <span style={{ opacity: 0.8 }}>特約文章 · native {isNativeCard ? "card" : "list row"} (fluid{fixed.length ? ` + ${band.filter((t) => t !== "fluid").join(" · ")}` : ""})</span>
        </div>
      </div>
    );
  }
  const style: React.CSSProperties = oop
    ? { position: "fixed", right: 8, bottom: 8, zIndex: 70, padding: "4px 8px", fontSize: 11, background: "rgba(0,0,0,.75)", color: "#fff", borderRadius: 4 }
    : isText
      ? { display: "inline-flex", alignItems: "center", padding: "2px 8px", fontSize: 12, border: "1px dashed #0d6b4b", color: "#0d6b4b", background: "rgba(13,107,75,.06)", borderRadius: 3 }
      : { width: "100%", maxWidth: w, aspectRatio: `${w} / ${h}`, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, border: "1px dashed #0d6b4b", background: "rgba(13,107,75,.06)", color: "#0d6b4b", fontSize: 11, lineHeight: 1.3, textAlign: "center", fontFamily: "ui-monospace, Menlo, monospace" };
  return (
    <div style={style} data-ad-preview="">
      <strong style={{ fontWeight: 600 }}>{code}</strong>
      <span>{oop ? "out-of-page" : isText ? "text / native" : `${w}×${h}`}</span>
      {!oop && !isText && band.length > 1 ? <span style={{ opacity: 0.7 }}>{band.join(" · ")}</span> : null}
      {grow && !oop && !isText ? <span style={{ opacity: 0.7 }}>{grow === true ? "grows with the creative" : `grows up to ${grow}px (billboard / pushdown)`}</span> : null}
    </div>
  );
}
